import { useState, useEffect } from 'react';
import { Search, Bell, CheckCircle2, Shield } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { ContentRecord, GovernanceRule } from '../../types/content';
import { MetricCard, Badge, EmptyState } from '../shared';

interface FlaggedRecord {
  record: ContentRecord;
  flags: string[];
  priority: 'high' | 'medium' | 'low';
}

export function Governance() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<ContentRecord[]>([]);
  const [rules, setRules] = useState<GovernanceRule[]>([]);
  const [flaggedRecords, setFlaggedRecords] = useState<FlaggedRecord[]>([]);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      // Load records
      const recordsQuery = query(
        collection(db, 'contentRecords'),
        where('userId', '==', user.uid)
      );
      const recordsSnap = await getDocs(recordsQuery);
      const recordsData = recordsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as ContentRecord);
      setRecords(recordsData);

      // Load governance rules
      const rulesQuery = query(
        collection(db, 'governanceRules'),
        where('userId', '==', user.uid)
      );
      const rulesSnap = await getDocs(rulesQuery);
      setRules(rulesSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as GovernanceRule));

      // Process flagged records
      const flagged = recordsData
        .filter(r => r.flags && r.flags.length > 0)
        .map(record => ({
          record,
          flags: record.flags,
          priority: record.status === 'suppressed' ? 'high' as const :
                    record.status === 'expiring' ? 'medium' as const :
                    'low' as const
        }))
        .sort((a, b) => {
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        });
      setFlaggedRecords(flagged);

    } catch (error) {
      console.error('Error loading governance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingCount = records.filter(r => r.status === 'pending').length;
  const expiringCount = records.filter(r => r.status === 'expiring').length;
  const suppressedCount = records.filter(r => r.status === 'suppressed').length;
  const approvedCount = records.filter(r => r.status === 'approved').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t2-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Content</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Governance</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t2-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t2-primary uppercase tracking-wide mb-1">Kodiac Content</p>
          <h1 className="text-3xl font-semibold text-gray-900">Governance</h1>
          <p className="text-sm text-gray-600 mt-1">Manage content approval workflows and quality controls</p>
        </div>

        {/* 4-Stat Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <MetricCard
            label="Pending Approval"
            value={pendingCount.toString()}
          />
          <MetricCard
            label="Expiring Soon"
            value={expiringCount.toString()}
          />
          <MetricCard
            label="Suppressed"
            value={suppressedCount.toString()}
          />
          <MetricCard
            label="Approved"
            value={approvedCount.toString()}
          />
        </div>

        {/* Flags Requiring Action */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Flags Requiring Action</h2>
            <span className="text-sm text-gray-600">{flaggedRecords.length} items</span>
          </div>

          {flaggedRecords.length === 0 ? (
            <EmptyState
              icon={Shield}
              title="No flagged content"
              description="All content meets governance standards"
            />
          ) : (
            <div className="space-y-2">
              {flaggedRecords.slice(0, 10).map(({ record, flags, priority }) => (
                <div
                  key={record.id}
                  className={`flex items-start justify-between p-3 rounded-lg border-l-4 ${
                    priority === 'high'
                      ? 'border-l-critical-red bg-red-50'
                      : priority === 'medium'
                      ? 'border-l-warning-amber bg-yellow-50'
                      : 'border-l-blue-400 bg-blue-50'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900">{record.title}</p>
                      <Badge variant="primary">{record.contentType}</Badge>
                      <Badge
                        variant={
                          priority === 'high' ? 'critical' : priority === 'medium' ? 'warning' : 'primary'
                        }
                      >
                        {priority}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {flags.map((flag, idx) => (
                        <span key={idx} className="text-xs text-gray-600 bg-white px-2 py-1 rounded border border-gray-200">
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button className="text-sm text-success-green hover:underline">Approve</button>
                    <button className="text-sm text-critical-red hover:underline">Suppress</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Governance Rules Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">Governance Rules</h2>
            <button className="px-4 py-2 bg-t2-primary text-white rounded-lg hover:bg-t2-dark text-sm">
              + Add Rule
            </button>
          </div>

          {rules.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="No governance rules"
              description="Create rules to automate content approval and quality checks"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-warm border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Rule Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Actions
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Modified
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rules.map(rule => (
                    <tr key={rule.id} className="hover:bg-surface-warm transition-colors">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="primary">{rule.type}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={rule.enabled ? 'success' : 'critical'}>
                          {rule.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600">
                          {rule.actions.length} {rule.actions.length === 1 ? 'action' : 'actions'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-600">
                          {rule.updatedAt.toDate().toLocaleDateString()}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Example Rules for Empty State */}
          {rules.length === 0 && (
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="p-4 bg-surface-warm rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Auto-Approval</h3>
                <p className="text-xs text-gray-600 mb-3">
                  Automatically approve content with AI readiness score &gt; 80
                </p>
                <button className="text-sm text-t2-primary hover:underline">Use Template</button>
              </div>
              <div className="p-4 bg-surface-warm rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Expiry Alert</h3>
                <p className="text-xs text-gray-600 mb-3">
                  Flag content for review 30 days before expiration
                </p>
                <button className="text-sm text-t2-primary hover:underline">Use Template</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
